import styled, { css } from '@xstyled/styled-components';
import React, { FC, useCallback, useEffect } from 'react';
import PointerImg from './bubble-pointer.svg';
import FullLlamaImg from './llama-circle.svg';
import { DefaultLlama } from './llamas/default';
import { SadLlama } from './llamas/sad';
import { TrainingState, useTrainingStore } from '~/training/state';
import { select } from '~/lib/select';

/*

  Unfortunately lots of CSS in here, that's to position the llama and the modal in various situations...

*/

export interface Props {
  horizontal?: 'left' | 'center' | 'right';
  vertical?: 'top' | 'center' | 'bottom';
  close?: () => void;
  variant?: 'sad' | 'default';
  showLlama?: 'show' | 'hide';
  /** Allow vertical scroll on short viewports (e.g. onboarding). */
  allowScroll?: boolean;
  /**
   * `modal` (default): the card is height-capped and its body scrolls.
   * `page`: the card grows to its content and the overlay scrolls instead —
   * for full-screen forms with nothing behind them. Requires `allowScroll`.
   */
  contentScroll?: 'modal' | 'page';
}

type PageScrollProps = { $pageScroll?: boolean };

// correspondance between alignment values and the flex property values
const vtable = {
  top: 'flex-start',
  center: 'center',
  bottom: 'flex-end'
};

const htable = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end'
};

const FullLlama = styled(FullLlamaImg)<Props>`
  position: absolute;
  bottom: -20px;
  margin-left: 0px;
  z-index: 3;
  left: 75px;

  @media (min-width: sm) and (max-width: 1024px) {
    left: 160px;
  }

  @media (max-width: sm) {
    z-index: -1;
    top: 0px;
    left: 50px;
  }
`;

// The llama picture

// The modal itself
const Modal = styled.div<PageScrollProps>`
  background: ${p => p.theme.colors.white};
  /* When brand secondary ≈ modal fill (e.g. #fff), outline the card (WCAG 1.4.11). */
  border: 1px solid var(--color-modal-edge, transparent);
  border-radius: lg;
  position: relative;
  z-index: 2;
  max-width: min(100%, calc(100vw - 2rem));
  /* Wrapper is a height-capped flex row that stretches Modal to its height;
     the column lets ModalClip/ModalContent shrink to that height and scroll
     instead of painting below the white card (CAT-1252). */
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-height: 100%;
  box-sizing: border-box;
  /* Keep overflow visible so Pointer (speech-bubble tail) can paint outside;
     body clipping lives on ModalClip. */
  overflow: visible;
  @media (max-width: sm) {
    width: 100%;
  }

  ${p =>
    p.$pageScroll &&
    css`
      max-height: none;
    `}
`;

/** Clips scrollable body + sticky Back to the modal's rounded rect. */
const ModalClip = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  border-radius: inherit;
  overflow: hidden;
  min-width: 0;
  min-height: 0;
  max-width: 100%;
`;

// This is the pointer thingy that comes from the modal and points at the llama
const Pointer = styled(PointerImg)<Props>`
  position: absolute;
  height: 30px;

  ${p =>
    p.horizontal === 'right'
      ? css`
          right: 40px;
          transform: scaleX(-1) rotate(0deg);
          bottom: -29px;
        `
      : p.horizontal === 'center'
        ? css`
            top: 100%;
            left: 50px;
          `
        : css`
            top: 50px;
            left: -27px;
            transform: rotate(90deg);
          `}

  @media (min-width: sm) and (max-width: 1024px) {
    ${p =>
      p.horizontal == 'center' &&
      css`
        left: 40%;
      `}
  }

  @media (max-width: sm) {
    top: unset;
    bottom: 100%;

    ${p =>
      p.horizontal === 'right'
        ? css`
            right: 160px;
            transform: scaleX(-1) scaleY(-1);
          `
        : css`
            left: 160px;
            transform: scaleY(-1);
          `}
  }
`;

// The modal's content so it can scroll (200% zoom / short viewports — EL–E18)
const ModalContent = styled.div<PageScrollProps>`
  position: relative;
  flex: 0 1 auto;
  min-width: 50px;
  min-height: 0;
  max-height: calc(100dvh - 8rem);
  /* Clip overflowing menu rows at 400% zoom without cutting Modal's Pointer.
     This is the primary scrollport — sticky CloseButton pins here (CAT-1252). */
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  box-sizing: border-box;

  @media (max-height: 400px) {
    /* Wrapper padding + llama eat most of a short CSS viewport (400% zoom) */
    max-height: calc(100dvh - 2.5rem);
  }

  /* overflow-x: hidden alone would turn this back into a scroll container. */
  ${p =>
    p.$pageScroll &&
    css`
      max-height: none;
      overflow: visible;
      @media (max-height: 400px) {
        max-height: none;
      }
    `}
`;

/* ModalContent is a flex item with a capped height, so it would resolve
   children's percentage heights (Flex variant="list" is height: 100%) and
   squeeze them — textareas collapse instead of the card scrolling (CAT-1634).
   This auto-height block keeps those percentages indefinite. */
const ModalBody = styled.div`
  min-width: 0;
`;

// This element adds padding to keep the llama on screen regardless of the modal's size
const Wrapper = styled.div<Props & PageScrollProps>`
  display: flex;
  position: relative;
  max-height: 100%;
  min-height: 0;
  box-sizing: border-box;
  ${p =>
    p.horizontal === 'right'
      ? css`
          padding-right: 60px;
          padding-top: 60px;
        `
      : p.horizontal === 'center'
        ? css`
            padding-left: 135px;
            padding-bottom: 100px;
          `
        : css`
            padding-left: 135px;
            padding-top: 60px;
          `}
  margin-left: ${p => (p.horizontal === 'center' ? '-150px' : 'unset')};

  @media (max-width: sm) {
    padding: 0;
    margin: 0;
    padding-top: 150px;
    width: 100%;
  }

  @media (max-height: 400px) {
    padding-top: 12px;
    padding-right: 12px;
    padding-left: 12px;
    padding-bottom: 12px;
    margin-left: 0;
  }

  /* Overlay aligns to flex-start in page mode; auto margins centre the card
     while it fits and collapse to 0 when it is taller, so its top stays
     reachable by scrolling (align-items: center would push it above 0). */
  ${p =>
    p.$pageScroll &&
    css`
      max-height: none;
      margin-top: ${p.vertical === 'top' ? '0' : 'auto'};
      margin-bottom: ${p.vertical === 'bottom' ? '0' : 'auto'};

      @media (max-width: sm) {
        margin-top: auto;
        margin-bottom: 0;
      }
    `}
`;

// This positions the modal on the screen
const Overlay = styled.div<Props & PageScrollProps>`
  position: fixed;
  display: flex;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: md;
  align-items: ${p => vtable[p.vertical!]};
  justify-content: ${p => htable[p.horizontal!]};
  pointer-events: none;
  overflow-x: hidden;
  overflow-y: ${p => (p.allowScroll ? 'auto' : 'hidden')};

  & > * {
    pointer-events: all;
  }

  @media (max-width: sm) {
    align-items: flex-end;
    justify-content: stretch;
  }

  ${p =>
    p.$pageScroll &&
    css`
      align-items: flex-start;
      @media (max-width: sm) {
        align-items: flex-start;
      }
    `}
`;

const selector = (s: TrainingState) => select(s, 'featureShowLlama');

export const LlamaModal: FC<Props> = ({
  showLlama,
  children,
  close,
  allowScroll,
  contentScroll = 'modal',
  ...props
}) => {
  const { featureShowLlama } = useTrainingStore(selector);
  const pageScroll = !!allowScroll && contentScroll === 'page';

  const escFunction = useCallback(event => {
    if (event.keyCode === 27) {
      if (close) {
        close();
      }
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', escFunction);

    return () => {
      document.removeEventListener('keydown', escFunction);
    };
  }, [escFunction]);

  return (
    <Overlay
      {...props}
      allowScroll={allowScroll}
      $pageScroll={pageScroll}
      className="LlamaModalOverlay"
    >
      <Wrapper
        {...props}
        $pageScroll={pageScroll}
        onClick={e => e.stopPropagation()}
        className="LlamaModalWrapper"
      >
        {showLlama == 'hide'
          ? false
          : props.horizontal === 'right'
            ? props.variant === 'sad'
              ? featureShowLlama && (
                  // CAT-1292 / EL–E189: decorative illustration — hide from AT.
                  <SadLlama
                    {...props}
                    onClick={close}
                    aria-hidden="true"
                    focusable="false"
                  />
                )
              : featureShowLlama && (
                  <DefaultLlama
                    {...props}
                    onClick={close}
                    aria-hidden="true"
                    focusable="false"
                  />
                )
            : featureShowLlama && (
                <FullLlama
                  {...props}
                  className="LlamaModalLlama"
                  onClick={close}
                  aria-hidden="true"
                  focusable="false"
                />
              )}
        <Modal className="modalStyleForDarkTheme" $pageScroll={pageScroll}>
          {showLlama == 'hide'
            ? false
            : featureShowLlama && (
                <Pointer
                  {...props}
                  className="pointerStyleForDarkTheme"
                  aria-hidden="true"
                  focusable="false"
                />
              )}
          <ModalClip>
            <ModalContent $pageScroll={pageScroll}>
              <ModalBody>{children}</ModalBody>
            </ModalContent>
          </ModalClip>
        </Modal>
      </Wrapper>
    </Overlay>
  );
};

LlamaModal.defaultProps = {
  horizontal: 'center',
  vertical: 'center'
};
